import styles from './CreditsCheckout.module.scss';
import { Button } from 'src/v2/commonComponents/Button';
import { PanelHeader } from 'src/v2/pages/subscription/components/CreditsCheckout/components/PanelHeader/PanelHeader';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useColorTheme,
  useCreditsAccountInfo,
  useCreditPack,
  useSession,
  useVisible,
} from 'src/hooks';
import { StripeElements } from 'src/v2/pages/subscription/components/CreditsCheckout/components/StripeElements';
import { LoadingPanel } from 'src/pages/PaymentBetaPage/components/LoadingPanel';
import {
  getNanosPrice,
  getPriceFromNanos,
  getPriceWithCurrency,
  getRoundedPriceWithCurrency,
  sendGTMEvent,
  stripePromise,
} from 'src/utils';
import { Elements } from '@stripe/react-stripe-js';
import { SummaryRow } from 'src/pages/PaymentBetaPage/components/SummaryRow';
import {
  useSetupIntentMutation,
  useUpdateCreditAccountMutation,
} from 'src/store/services';
import {
  CREDITS_SAVED_SUCCESS_MESSAGE,
  PAYMENT_ERROR_MESSAGE,
} from 'src/constants';
import { toast } from 'react-toastify';
import { GTMEvent, TokenApiHttpErrorWithSeveralErrorMessages } from 'src/types';
import { PackOptionsTooltip } from './components/PackOptionsTooltip';

type CreditsCheckoutProps = {
  onClose: () => void;
  onPaymentProcessing: (value: boolean) => void;
};

export const MIN_AUTO_PAY_THRESHOLD_VALUE = 25;

export const CreditsCheckout = ({
  onClose,
  onPaymentProcessing,
}: CreditsCheckoutProps) => {
  const { isDarkTheme } = useColorTheme();
  const {
    appUser: { user_id },
  } = useSession();
  const {
    isCreditAccountInfoLoading,
    creditAccountInfo,
    isCreditAccountConfigured,
  } = useCreditsAccountInfo();

  const { formattedPacks } = useCreditPack();
  const minAutoPayValue = useMemo(
    () => formattedPacks[0].value,
    [formattedPacks],
  );
  const [autoPayAmount, setAutoPayAmount] = useState<number>(minAutoPayValue);

  const autoPayThresholdAmount = MIN_AUTO_PAY_THRESHOLD_VALUE;
  const [isReadyStripeElements, setReadyStripeElements] =
    useState<boolean>(false);
  const [setupIntentClientSecret, setSetupIntentClientSecret] =
    useState<string>('');
  const [isSetupIntentFailed, setIsSetupIntentFailed] =
    useState<boolean>(false);
  const hasRequestedSetupIntentRef = useRef<boolean>(false);

  const [updateCreditAccount] = useUpdateCreditAccountMutation();
  const [setupIntent] = useSetupIntentMutation();

  const title = useMemo(() => {
    return creditAccountInfo?.reload_config?.auto_reload_enabled
      ? 'Manage credits'
      : 'Purchase credits';
  }, [creditAccountInfo?.reload_config?.auto_reload_enabled]);

  const {
    isVisible: isElementsLoading,
    onVisibilityRemove: onElementsLoadingRemove,
    onVisibilitySet: onElementsLoadingSet,
  } = useVisible(true);

  useEffect(() => {
    if (
      isCreditAccountConfigured ||
      setupIntentClientSecret ||
      isSetupIntentFailed
    ) {
      onElementsLoadingRemove();
    } else {
      onElementsLoadingSet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCreditAccountConfigured,
    isReadyStripeElements,
    setupIntentClientSecret,
    isSetupIntentFailed,
  ]);

  useEffect(() => {
    const reloadAmount = getPriceFromNanos(
      creditAccountInfo?.reload_config?.reload_amount_nanos,
    );

    setAutoPayAmount(reloadAmount > 0 ? reloadAmount : minAutoPayValue);
  }, [creditAccountInfo?.reload_config, minAutoPayValue]);

  useEffect(() => {
    if (
      isCreditAccountConfigured ||
      isCreditAccountInfoLoading ||
      setupIntentClientSecret ||
      isSetupIntentFailed ||
      hasRequestedSetupIntentRef.current
    ) {
      return;
    }

    let isMounted = true;
    hasRequestedSetupIntentRef.current = true;

    const fetchSetupIntent = async () => {
      try {
        const result = await setupIntent({ user_id }).unwrap();

        if (!isMounted) {
          return;
        }

        setSetupIntentClientSecret(result.client_secret);
        setIsSetupIntentFailed(false);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setIsSetupIntentFailed(true);
        toast.error(PAYMENT_ERROR_MESSAGE);
      }
    };

    fetchSetupIntent();

    return () => {
      isMounted = false;
      hasRequestedSetupIntentRef.current = false;
    };
  }, [
    isCreditAccountConfigured,
    isCreditAccountInfoLoading,
    isSetupIntentFailed,
    setupIntent,
    setupIntentClientSecret,
    user_id,
  ]);

  const isPaymentDisabled = useMemo(() => {
    return (
      autoPayAmount < minAutoPayValue ||
      autoPayThresholdAmount < MIN_AUTO_PAY_THRESHOLD_VALUE ||
      autoPayThresholdAmount > autoPayAmount
    );
  }, [autoPayAmount, autoPayThresholdAmount, minAutoPayValue]);

  const isUpdateDisabled = useMemo(() => {
    if (!creditAccountInfo || !creditAccountInfo?.reload_config) {
      return true;
    }
    return (
      autoPayAmount ===
        getPriceFromNanos(
          creditAccountInfo?.reload_config?.reload_amount_nanos,
        ) &&
      autoPayThresholdAmount ===
        getPriceFromNanos(
          creditAccountInfo?.reload_config?.reload_threshold_amount_nanos,
        ) &&
      creditAccountInfo?.reload_config?.auto_reload_enabled
    );
  }, [autoPayAmount, autoPayThresholdAmount, creditAccountInfo]);

  const handleUpdateCreditAccount = async () => {
    try {
      onPaymentProcessing(true);
      await updateCreditAccount({
        user_id,
        credit_account: creditAccountInfo?.account_id || '',
        paymentInfo: {
          reload_config: {
            auto_reload_enabled: true,
            reload_amount_nanos: getNanosPrice(autoPayAmount),
            reload_threshold_amount_nanos: getNanosPrice(
              autoPayThresholdAmount,
            ),
          },
        },
      }).unwrap();
      sendGTMEvent(GTMEvent.CHANGE_CREDITS);
      toast.success(CREDITS_SAVED_SUCCESS_MESSAGE);
      onClose();
    } catch (e) {
      toast.error(
        (e as TokenApiHttpErrorWithSeveralErrorMessages)?.data?.detail
          ? (e as TokenApiHttpErrorWithSeveralErrorMessages).data.detail[0]
              ?.msg || ''
          : PAYMENT_ERROR_MESSAGE,
      );
    } finally {
      onPaymentProcessing(false);
    }
  };

  return (
    <div className={styles.root}>
      {(isElementsLoading || isCreditAccountInfoLoading) && (
        <LoadingPanel
          onClose={onClose}
          title={title}
          classname={styles.loadingScreen}
        />
      )}
      <PanelHeader onClose={onClose} title={title} />
      <div className={styles.mainContainer}>
        <div className={styles.packWrapper}>
          <div className={styles.topPackSectionRow}>
            <span className={styles.packLabel}>Pack options</span>
            <span className={styles.packMinValue}>Min: ${minAutoPayValue}</span>
          </div>

          <PackOptionsTooltip
            autoPayAmount={autoPayAmount}
            setAutoPayAmount={setAutoPayAmount}
          />

          <span className={styles.footNotePackSection}>
            Automatically refill your SuperNinja credits when your balance drops
            to ${MIN_AUTO_PAY_THRESHOLD_VALUE}
          </span>
        </div>
        <hr className={styles.divider} />
        <div className={styles.summaryWrapper}>
          <SummaryRow
            label="Subtotal"
            value={getPriceWithCurrency(autoPayAmount)}
          />
          <SummaryRow
            label="Tax & fees"
            value={getRoundedPriceWithCurrency(0)}
          />
          <div className={styles.checkoutResultSectionRow}>
            <span>Due today (USD)</span>
            <span>{getPriceWithCurrency(autoPayAmount)}</span>
          </div>
        </div>
        <hr className={styles.divider} />
        <div className={styles.paymentWrapper}>
          {isCreditAccountConfigured || isCreditAccountInfoLoading ? (
            <Button
              onClick={handleUpdateCreditAccount}
              className={styles.saveButton}
              disabled={isPaymentDisabled || isUpdateDisabled}
            >
              Save changes
            </Button>
          ) : (
            stripePromise && (
              <>
                <h4 className={styles.paymentHeader}>Payment method</h4>
                {setupIntentClientSecret ? (
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret: setupIntentClientSecret,
                      appearance: {
                        theme: isDarkTheme ? 'night' : 'stripe',
                        variables: {
                          borderRadius: '12px',
                        },
                      },
                    }}
                  >
                    <StripeElements
                      onReady={() => setReadyStripeElements(true)}
                      onPaymentProcessing={onPaymentProcessing}
                      disablePayment={isPaymentDisabled}
                      amount={autoPayAmount}
                      thresholdAmount={autoPayThresholdAmount}
                    />
                  </Elements>
                ) : null}
              </>
            )
          )}
          <span className={styles.footNote}>
            By clicking "Confirm & Pay," you authorize automatic replenishment
            of your account when credits fall below your specified threshold.
            You can cancel auto-pay anytime.
          </span>
        </div>
      </div>
    </div>
  );
};
